import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = "https://api.athenex.xyz";

interface AuthContextType {
  isAuthenticated: boolean;
  authError: string | null;
  isLoading: boolean;
  loginWithKey: (apiKey: string) => Promise<boolean>;
  logout: () => void;
  getAuthHeaders: () => Record<string, string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [initializationComplete, setInitializationComplete] = useState<boolean>(false);

  // Check for existing authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      const storedApiKey = localStorage.getItem('API-key');
      
      if (storedApiKey) {
        try {
          // Test the API key with a single request
          await axios.post(
            `${API_BASE_URL}/dta/login`,
            {}, 
            {
              headers: { 
                "API-Key": storedApiKey,
                "Content-Type": "application/json"
              }
            }
          );
          
          console.log("Auth initialized with stored API key");
          setIsAuthenticated(true);
          setAuthError(null);
        } catch (error) {
          console.error("Stored API key is invalid:", error);
          // Clear the invalid API key
          localStorage.removeItem('API-key');
          setIsAuthenticated(false);
          setAuthError("Session expired. Please login again.");
        }
      } else {
        setIsAuthenticated(false);
      }
      
      setIsLoading(false);
      setInitializationComplete(true);
    };
    
    checkAuth();
    
    return () => {
      // Cleanup if needed
    };
  }, []);

  const loginWithKey = async (apiKey: string): Promise<boolean> => {
    setIsLoading(true);
    setAuthError(null);
    
    try {
      // Test the API key with a request to the login endpoint
      await axios.post(
        `${API_BASE_URL}/dta/login`,
        {}, 
        {
          headers: { 
            "API-Key": apiKey,
            "Content-Type": "application/json"
          }
        }
      );
      
      // If successful, store the API key and update state
      localStorage.setItem('API-key', apiKey);
      setIsAuthenticated(true);
      setIsLoading(false);
      return true;
    } catch (error: any) {
      console.error("Login error:", error);
      const errorMessage = error.response?.data?.message || 
                          (error.response?.status === 403 ? "Access forbidden. Invalid API key." : 
                          "Login failed. Please try again.");
      setAuthError(errorMessage);
      setIsAuthenticated(false);
      setIsLoading(false);
      return false;
    }
  };
  
  const logout = () => {
    const apiKey = localStorage.getItem('API-key');
    
    // Call logout endpoints if we have an API key
    if (apiKey) {
      // Use synchronous XMLHttpRequest for logout to ensure it completes
      try {
        // Logout from DTA
        const dtaLogout = new XMLHttpRequest();
        dtaLogout.open("POST", `${API_BASE_URL}/dta/logout`, false); // false for synchronous
        dtaLogout.setRequestHeader("API-Key", apiKey);
        dtaLogout.send();
        
        // Logout from file_manager
        const fileManagerLogout = new XMLHttpRequest();
        fileManagerLogout.open("POST", `${API_BASE_URL}/file_manager/logout`, false); // false for synchronous
        fileManagerLogout.setRequestHeader("API-Key", apiKey);
        fileManagerLogout.send();
      } catch (e) {
        console.error("Error during logout:", e);
      }
    }
    
    // Clear local storage and reset state
    localStorage.removeItem('API-key');
    setIsAuthenticated(false);
    setAuthError(null);
  };
  
  const getAuthHeaders = (): Record<string, string> => {
    const apiKey = localStorage.getItem('API-key');
    return apiKey ? { "API-Key": apiKey } : {};
  };
  
  // Cleanup on unmount - attempt logout
  useEffect(() => {
    const handleBeforeUnload = () => {
      logout();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  
  const value = {
    isAuthenticated,
    authError,
    isLoading,
    loginWithKey,
    logout,
    getAuthHeaders
  };
  
  if (!initializationComplete) {
    return <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
    </div>;
  }
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
